import React from 'react'
import { Box, IconButton, Tooltip, Divider, styled, Stack } from '@mui/material'

// Define the shape of a dynamic action button
export interface ActionButtonConfig {
	key: string
	icon: React.ReactNode
	label: string
	onClick: () => void
	color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
	disabled?: boolean
}

interface ModernActionGroupProps {
	actions: ActionButtonConfig[]
	size?: 'small' | 'medium'
}

const StyledGroupContainer = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'size',
})<{ size: 'small' | 'medium' }>(({ theme, size }) => ({
	display: 'inline-flex',
	alignItems: 'center',
	border: `1px solid ${theme.palette.divider}`,
	borderRadius: size === 'small' ? '8px' : '12px',
	padding: size === 'small' ? '2px' : '4px',
	backgroundColor: theme.palette.background.paper,
	transition: 'box-shadow 0.2s ease-in-out',
	'&:hover': {
		borderColor: theme.palette.primary.main,
		boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
	},
}))

const ModernActionGroup: React.FC<ModernActionGroupProps> = ({ actions, size = 'medium' }) => {
	const iconSize = size === 'small' ? 16 : 20
	const padding = size === 'small' ? 0.5 : 1

	return (
		<StyledGroupContainer size={size}>
			<Stack
				direction="row"
				spacing={0.5}
				divider={<Divider orientation="vertical" flexItem sx={{ my: size === 'small' ? 0.8 : 1 }} />}
			>
				{actions.map((action) => (
					<Tooltip key={action.key} title={action.label} arrow>
						<IconButton
							size="small"
							onClick={action.onClick}
							disabled={action.disabled}
							color={action.color || 'default'}
							sx={{
								p: padding,
								borderRadius: size === 'small' ? '6px' : '8px',
								'&:hover': {
									backgroundColor: 'action.hover',
								},
							}}
						>
							{/* Clone the icon to inject size styling */}
							{React.cloneElement(action.icon as React.ReactElement, {
								sx: { fontSize: iconSize },
							})}
						</IconButton>
					</Tooltip>
				))}
			</Stack>
		</StyledGroupContainer>
	)
}

export default ModernActionGroup
